import {
  Device,
  DevicesMetricsResponse as DevicesMetricsResponseDto,
  MdmDevicesMetricsResponse as MdmDevicesMetricsResponseDto,
  OsTypeEnrollmentBreakdown as OsTypeEnrollmentBreakdownDto,
} from '@electricjs/core_entity-client';

import { mockMac } from '@/mocks/data/assetData';
import { mockOrganizationId } from '@/mocks/data/siteData';
import { DeviceReportingDevice, EndOfLifeDto } from '@/types/devices';

export const singleDevice: Device[] = [
  {
    serial_number: 'serial-number-1',
    hardware_model: 'hardware-model-1',
    os_type: 'darwin',
    organization_id: 'org-id-1',
    assigned_to: '',
    id: 'device-id-1',
  },
];

// Device backing `mockMac`, in the shape returned by the devices endpoints.
export const mockMacDevice: Device = {
  id: mockMac.device!.id,
  serial_number: mockMac.serialNumber,
  hardware_model: mockMac.device!.hardwareModel,
  os_type: mockMac.device!.osType,
  organization_id: mockOrganizationId,
  assigned_to: mockMac.assignedTo,
  asset_id: mockMac.id,
  lock_status: mockMac.device!.lockStatus,
  lock_pin: mockMac.device!.lockPin,
  mdm_enrolled_at: mockMac.device!.mdmEnrolledAt,
};

const emptyOsBreakdown: OsTypeEnrollmentBreakdownDto = {
  total: 0,
  versions: 0,
  at_eol: 0,
  near_eol: 0,
  supported: 0,
  unknown: 0,
  version_details: [],
};

const macOsBreakdown: OsTypeEnrollmentBreakdownDto = {
  total: 8,
  versions: 2,
  at_eol: 1,
  near_eol: 1,
  supported: 6,
  unknown: 0,
  version_details: [
    { os_name: 'Sonoma', os_version: '14.4.0', count: 6 },
    { os_name: 'Monterey', os_version: '12.7.0', count: 2 },
  ],
};

const windowsOsBreakdown: OsTypeEnrollmentBreakdownDto = {
  total: 4,
  versions: 1,
  at_eol: 0,
  near_eol: 0,
  supported: 4,
  unknown: 0,
  version_details: [
    { os_name: 'Windows 11', os_version: '10.0.22631', count: 4 },
  ],
};

export const mockMdmDevicesMetrics: MdmDevicesMetricsResponseDto = {
  devices: {
    managed_devices: 12,
    unmanaged_devices: 3,
    unmanaged_devices_ids: [mockMacDevice.id],
    assigned_enrolled: {
      darwin: macOsBreakdown,
      windows: windowsOsBreakdown,
      android: emptyOsBreakdown,
      ios: emptyOsBreakdown,
    },
    unassigned_enrolled: {
      darwin: emptyOsBreakdown,
      windows: emptyOsBreakdown,
      android: emptyOsBreakdown,
      ios: emptyOsBreakdown,
    },
  },
  employees_without_assets: 2,
};

export const mockDevicesMetrics: DevicesMetricsResponseDto = {
  employees: {
    total: 20,
    active: { total: 17, with_devices: 14, without_devices: 3 },
    inactive: { total: 2, with_devices: 1, without_devices: 1 },
    unknown: { total: 1 },
  },
  devices: {
    total: 18,
    active: {
      total: 15,
      assigned_to_active_employees: {
        total: 12,
        mdm_enrolled: 10,
        mdm_unenrolled: 2,
      },
      assigned_to_inactive_employees: {
        total: 1,
        mdm_enrolled: 1,
        mdm_unenrolled: 0,
      },
      unassigned: { total: 2, mdm_enrolled: 1, mdm_unenrolled: 1 },
    },
    inactive: { archived: 1, donated: 0, sold: 1, disposed: 1, missing: 0 },
  },
};

// Raw device-reporting response (snake_case) for the fake-device endpoint.
export const mockReportingDevice: DeviceReportingDevice = {
  id: mockMacDevice.id,
  organization_id: mockOrganizationId,
  serial_number: mockMacDevice.serial_number,
  hardware_model: mockMacDevice.hardware_model,
  os_type: mockMacDevice.os_type,
  os_version: '14.4.0',
  os_name: 'Sonoma',
  created_at: mockMac.createdAt,
  updated_at: mockMac.updatedAt,
};

export const mockOsEndOfLife: EndOfLifeDto[] = [
  {
    product: 'macos',
    version: '12',
    eol_date: '2024-09-16',
    codename: 'Monterey',
  },
  {
    product: 'macos',
    version: '14',
    eol_date: '2026-09-30',
    codename: 'Sonoma',
  },
  { product: 'windows', version: '10', eol_date: '2025-10-14' },
  { product: 'windows', version: '11', eol_date: '2027-10-12' },
];
